import { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Item, ItemProps } from ".";

interface Option {
    label: string;
    value: string;
}

type SingleSelectProps = {
    multiple?: false;
    value?: string;
    onChange?: (value: string | "") => void;
};

type MultiSelectProps = {
    multiple: true;
    value?: string[];
    onChange?: (value: string[]) => void;
};

interface SelectBaseProps extends ItemProps {
    placeholder?: string;
    options: Option[];
    loading?: boolean;

    onSearch?: (keyword: string, page: number) => void | Promise<void>;

    onLoadMore?: (keyword: string, page: number) => boolean | Promise<boolean>;

    onAdd?: (keyword: string) => void;
    labelAdd?: string;
}

type SelectProps = SelectBaseProps & (SingleSelectProps | MultiSelectProps);

const Select: FC<SelectProps> = ({
    label,
    required,
    col,
    placeholder = "Chọn...",
    options,
    multiple = false,
    value,
    onChange,
    error,
    loading,
    onSearch,
    onLoadMore,
    itemClassName,
    onAdd,
    labelAdd = "Thêm",
}) => {

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const [highlightIndex, setHighlightIndex] = useState(-1);
    const [dropUp, setDropUp] = useState(false);

    const firstLoadRef = useRef<boolean>(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const loadingMoreRef = useRef(false);

    const filteredOptions = useMemo(() => {

        if (onSearch) return options;

        return options.filter(opt =>
            opt.label?.toLowerCase()?.includes(search.toLowerCase())
        );

    }, [options, search, onSearch]);

    const isSelected = (val: string) => {

        if (multiple && Array.isArray(value)) {
            return value.includes(val);
        }

        return value === val;

    };

    const handleSelect = (val: string) => {

        if (!multiple) {

            (onChange as (v: string | "") => void)?.(val);
            setOpen(false);

        } else {

            const current = Array.isArray(value) ? value : [];

            if (current.includes(val)) {

                (onChange as (v: string[]) => void)?.(
                    current.filter(v => v !== val)
                );

            } else {

                (onChange as (v: string[]) => void)?.([...current, val]);

            }

        }

    };

    const handleClear = () => {

        if (!multiple) {
            (onChange as (v: string | "") => void)?.("");
        } else {
            (onChange as (v: string[]) => void)?.([]);
        }

    };

    const handleSearch = async (keyword: string) => {

        setSearch(keyword);

        setPage(1);
        setHasMore(true);

        await onSearch?.(keyword, 1);

    };

    const handleLoadMore = async () => {

        if (!onLoadMore) return;
        if (!hasMore) return;
        if (loadingMoreRef.current) return;

        loadingMoreRef.current = true;

        const nextPage = page + 1;

        const result = await onLoadMore(search, nextPage);

        if (result) {
            setPage(nextPage);
        } else {
            setHasMore(false);
        }

        loadingMoreRef.current = false;

    };

    const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {

        const el = e.currentTarget;

        const reachedBottom =
            el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

        if (reachedBottom) {
            await handleLoadMore();
        }

    };

    useEffect(() => {

        const handleClickOutside = (e: MouseEvent) => {

            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {

                setOpen(false);
                setSearch("");

            }

        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => document.removeEventListener("mousedown", handleClickOutside);

    }, []);

    useEffect(() => {

        if (open) {

            setTimeout(() => {

                searchRef.current?.focus();

            }, 50);

        }

    }, [open]);

    useEffect(() => {

        if (!open || !wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();

        const spaceBelow = window.innerHeight - rect.bottom;

        setDropUp(spaceBelow < 260);

    }, [open]);

    const handleKeyDown = (e: React.KeyboardEvent) => {

        if (!open) return;

        if (e.key === "ArrowDown") {

            e.preventDefault();

            setHighlightIndex(i =>
                Math.min(i + 1, filteredOptions.length - 1)
            );

        }

        if (e.key === "ArrowUp") {

            e.preventDefault();

            setHighlightIndex(i =>
                Math.max(i - 1, 0)
            );

        }

        if (e.key === "Enter") {

            e.preventDefault();

            const option = filteredOptions[highlightIndex];

            if (option) {
                handleSelect(option.value);
            }

        }

        if (e.key === "Escape") {
            setOpen(false);
        }

    };

    useEffect(() => {

        if (!onSearch) return;
        if (firstLoadRef.current) return;
        if (options.length > 0) return;

        firstLoadRef.current = true;

        setPage(1);
        setHasMore(true);

        onSearch("", 1);

    }, [options.length, onSearch]);

    const showAddButton = useMemo(() => {

        if (!onAdd) return false;
        if (!search.trim()) return false;

        return filteredOptions.length === 0;

    }, [filteredOptions.length, onAdd, search]);

    return (
        <Item
            col={col}
            label={label}
            required={required}
            error={error}
            itemClassName={itemClassName}
        >
            <div
                className="relative group/select"
                ref={wrapperRef}
                onKeyDown={handleKeyDown}
            >
                <div
                    className={`w-full border px-4 ${multiple && Array.isArray(value) && value.length > 0 ? "py-1" : "py-2"} rounded bg-white flex items-center cursor-pointer ${error ? "border-red-500" : "border-gray-300"}`}
                    onClick={() => setOpen(o => !o)}
                >
                    {multiple ? (
                        Array.isArray(value) && value.length > 0 ? (
                            <Fragment>
                                <span className="flex-1 flex flex-wrap gap-2">
                                    {value.map(v => {

                                        const label = options.find(opt => opt.value === v)?.label || v;

                                        return (
                                            <span
                                                key={v}
                                                className="px-4 py-1 rounded-full bg-gray-200 inline-block"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSelect(v);
                                                }}
                                            >
                                                {label}
                                            </span>
                                        );

                                    })}
                                </span>

                                <button
                                    type="button"
                                    className="ml-auto p-3 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-red-100 opacity-0 group-hover/select:opacity-100 transition"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleClear();
                                    }}
                                >
                                    ✕
                                </button>
                            </Fragment>
                        ) : (
                            <span className="text-gray-400">{placeholder}</span>
                        )
                    ) : value ? (
                        <Fragment>

                            <span>
                                {options.find(opt => opt.value === value)?.label || value}
                            </span>

                            <button
                                type="button"
                                className="ml-auto p-3 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-red-100 opacity-0 group-hover/select:opacity-100 transition"
                                onClick={e => {
                                    e.stopPropagation();
                                    handleClear();
                                }}
                            >
                                ✕
                            </button>

                        </Fragment>
                    ) : (
                        <span className="text-gray-400">{placeholder}</span>
                    )}
                </div>

                {open && (
                    <div className={`absolute z-10 w-full bg-white border rounded shadow ${dropUp ? "bottom-full mb-1" : "top-full mt-1"}`}>

                        <div className="p-2">
                            <input
                                ref={searchRef}
                                type="text"
                                value={search}
                                placeholder="Tìm kiếm..."
                                className="w-full border px-2 py-1 rounded"
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </div>

                        <div
                            ref={listRef}
                            className="overflow-auto max-h-72"
                            onScroll={handleScroll}
                        >

                            {filteredOptions.map((opt, index) => {

                                const selected = isSelected(opt.value);
                                const highlighted = index === highlightIndex;

                                return (
                                    <div
                                        key={opt.value}
                                        className={`px-4 py-2 flex items-start justify-between cursor-pointer
                                        ${selected ? "bg-blue-100" : ""}
                                        ${highlighted ? "bg-gray-100" : ""}`}
                                        onClick={() => handleSelect(opt.value)}
                                    >
                                        <span>{opt.label}</span>

                                        {selected && (
                                            <span className="text-blue-500 ml-2">✓</span>
                                        )}
                                    </div>
                                );

                            })}

                            {loading && (
                                <div className="text-center p-2 text-gray-400">
                                    Đang tải...
                                </div>
                            )}

                            {showAddButton && (
                                <div
                                    className="px-4 py-2 cursor-pointer text-blue-600 hover:bg-blue-50 border-t"
                                    onClick={() => onAdd?.(search)}
                                >
                                    {labelAdd}: <b>{search}</b>
                                </div>
                            )}

                        </div>

                    </div>
                )}

            </div>
        </Item>
    );
};

export default Select;